import { FC, useEffect, useState } from 'react';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import PlanCard from '../PlanCard';
import { Values } from '../../pages/PersonalizedForm/PersonalizedForm';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setMembers, setPlans, setSelectedPlan, setSelectedPlanId } from '../../store/slices/planSlice';
import { useGetPlansQuery } from '../../store/services/authApi';
import { useUpdatePlanMutation } from '../../store/services/customizeOptionsApi';

import styles from './Plans.module.scss';

type PlanAdvantage = {
  id: number,
  content: string,
  measure: boolean | string,
}

export interface IPlan {
  id: number;
  group: "Single" | "Family" | "Big Family",
  type: "Basic" | "Premium" | "Elite",
  price_in_cents: number;
  paddle_id: string;
  benefits: string[];
  advantages: PlanAdvantage[];
}

type PlansProps = {
  values: Values | { plan: number };
  hideHeader?: boolean;
  isUpdateOnClick?: boolean;
  onChangePlan: <T = string | React.ChangeEvent<any>>(field: T) => T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
  onTablePlansModalOpen?: (args: IPlan[]) => void;
}

const Plans: FC<PlansProps> = ({ values, hideHeader = false, isUpdateOnClick = false, onChangePlan, onTablePlansModalOpen }) => {
  const dispatch = useAppDispatch();
  const {members} = useAppSelector((state) => state.plan);
  const [selectedGroup, setSelectedGroup] = useState<"Single" | "Family" | "Big Family">("Single");
  const { data: plans } = useGetPlansQuery({ group: selectedGroup });
  const [updatePlanMutation] = useUpdatePlanMutation();

  const [selected, setSelected] = useState('Single');
  const [isOn, setIsOn] = useState(members === 5);

  useEffect(() => {
    if (plans) {
      dispatch(setPlans(plans));
    }
  }, [plans]);

  useEffect(() => {

    return () => {
      dispatch(setMembers(3));
    }
  }, [])

  const toggleSwitch = () => {
    setIsOn((prev) => !prev);
    if (selected === 'Family' && !isOn) {
      setSelectedGroup("Big Family");
    } else {
      setSelectedGroup("Family");
    }
  };

  const handleToggleChange = (_: React.MouseEvent<HTMLElement>, newSelection: "Single" | "Family" | "Big Family") => {
    if (newSelection !== null) {
      setSelected(newSelection);
      setSelectedGroup(newSelection);
    }
  };

  const handlePlanClick = async (id: number) => {

    if (isUpdateOnClick) {
      try {
        const res = await updatePlanMutation({plan_id: id});
        if (res.data) {
            const url = new URL(res.data.checkout_url);
            const transactionId = url.searchParams.get('_ptxn');
            window.open(`/checkout.html?&_ptxn=${transactionId}`, '_blank');
        }
        dispatch(setSelectedPlanId({ id, members: isOn ? 5 : 3 }));
      } catch (error) {
        console.error(error);
      }
    }
  }

  const handleSelectedPlan = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPlan = plans.find((plan: IPlan) => plan.id === Number(e.target.value));

    onChangePlan(e);
    dispatch(
      setSelectedPlan({
        type: selectedPlan.type,
        plan: selectedPlan.id,
        members: isOn ? 5 : 3
      })
    );
  }

  return (
    <Box id={styles.plans}>
      <Box className={`${styles.header} ${hideHeader ? styles.hideHeader : ''}`}>
        <Typography variant='h5Bold' sx={{textAlign: 'center'}}>Find the right plan for you</Typography>
        <Typography variant='h10' sx={{textAlign: 'center'}}>Upgrade to experience the full power of TravGenius</Typography>
      </Box>

      <ToggleButtonGroup
        value={selected}
        exclusive
        onChange={handleToggleChange}
        className={styles.toggleGroup}
      >
        <ToggleButton value="Single" className={`${selected === 'Single' && styles.selected} ${styles.toggleButton}`}>
          {selected === 'Single' ? (
            <Typography variant='h11Bold'>Single</Typography>
          ) : (
            <Typography variant='h11Semibold'>Single</Typography>
          )}
        </ToggleButton>
        <ToggleButton value="Family" className={`${selected === 'Family' && styles.selected} ${styles.toggleButton}`}>
          {selected === 'Family' ? (
            <Typography variant='h11Bold'>Family</Typography>
          ) : (
            <Typography variant='h11Semibold'>Family</Typography>
          )}
        </ToggleButton>
      </ToggleButtonGroup>

      {selected === 'Family' && 
        <Box className={styles.membersCount}>
          <Typography variant='h11'>Up to 3 members</Typography>
          <Box className={styles.toggleContainer} onClick={toggleSwitch}>
            <Box className={`${styles.toggle} ${isOn ? styles.toggleOn : ""}`}>
              <Box className={styles.toggleCircle}></Box>
            </Box>
          </Box>
          <Typography variant='h11'>Up to 5 members</Typography>
        </Box>
      }

      <FormControl component="fieldset" className={styles.radioGroupWrapper}>
        <RadioGroup name="plan" className={styles.radioGroup} value={values.plan} onChange={handleSelectedPlan}>
        {plans && plans.map((plan : IPlan) => (
          <FormControlLabel
              key={plan.id}
              value={plan.id}
              onClick={() => handlePlanClick(Number(plan.id))}
              control={<Radio sx={{ display: "none" }} />}
              label={
                <PlanCard plan={plan} selected={Number(values.plan) === plan.id}/>
              }
            />
        ))}
        </RadioGroup>
      </FormControl>

      {onTablePlansModalOpen && <Typography variant='h10' className={styles.plansFeatures} onClick={() => onTablePlansModalOpen(plans)}>
        Compare plans & features
      </Typography>}
    </Box>
  )
}

export default Plans;
